import React, { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot, addDoc, deleteDoc, doc, serverTimestamp, getDocs } from '../../lib/auditedFirestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../lib/firebase';
import Spinner from '../../components/Spinner';
import * as XLSX from 'xlsx';

const HomeworkManager = ({ paper, onBack, setModal, onOpenHomework }) => { 
    const [homeworks, setHomeworks] = useState([]);
    const [title, setTitle] = useState('');
    const [isUploading, setIsUploading] = useState(false);
    
    // --- Submissions View State ---
    const [selectedHomework, setSelectedHomework] = useState(null);
    const [submissions, setSubmissions] = useState([]);
    const [allStudents, setAllStudents] = useState([]);
    const [isLoadingStats, setIsLoadingStats] = useState(false);

    // 1. Fetch Homeworks for this Paper
    useEffect(() => {
        const q = query(collection(db, "homeworks"), where("paperId", "==", paper.id));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            // Sort newest first
            data.sort((a, b) => (b.createdAt?.toMillis() || 0) - (a.createdAt?.toMillis() || 0));
            setHomeworks(data);
        });
        return () => unsubscribe();
    }, [paper.id]);

    // 2. Upload New Homework & Trigger Notification
    const handleUpload = async (e) => {
        e.preventDefault();
        const file = e.target.file.files[0];
        if (!file || !title) return;

        setIsUploading(true);
        try {
            let fileType = 'image';
            if (file.type.includes('pdf')) fileType = 'pdf';

            // Store in a homeworks folder in storage
            const storageRef = ref(storage, `homeworks/${paper.id}/${Date.now()}-${file.name}`);
            const snapshot = await uploadBytes(storageRef, file);
            const url = await getDownloadURL(snapshot.ref);

            await addDoc(collection(db, "homeworks"), {
                paperId: paper.id,
                title: title,
                url: url,
                type: fileType,
                createdAt: serverTimestamp()
            });

            // --- ADD NOTIFICATION ---
            await addDoc(collection(db, "notifications"), {
                type: 'homework',
                title: `New Homework: ${paper.name}`,
                message: `A new assignment "${title}" has been added.`,
                paperId: paper.id,
                targetAudience: ['student'],
                createdAt: serverTimestamp()
            });

            setTitle('');
            e.target.reset(); 
            setModal({ show: true, message: "Homework assigned & students notified!" });
        } catch (error) {
            console.error(error);
            setModal({ show: true, message: "Upload failed: " + error.message });
        } finally {
            setIsUploading(false);
        }
    };

    // 3. Delete Homework
    const handleDelete = async (id) => {
        if(!window.confirm("Delete this assignment? Student submissions will remain but be orphaned.")) return;
        try {
            await deleteDoc(doc(db, "homeworks", id));
            setModal({ show: true, message: "Homework deleted." });
        } catch (e) {
            console.error(e);
            setModal({ show: true, message: "Error deleting homework." });
        }
    };

    // 4. View Submissions & Generate Report Logic
    const handleViewSubmissions = async (hw) => {
        setSelectedHomework(hw);
        setIsLoadingStats(true);
        
        try {
            // A. Fetch All Students assigned to this Paper
            const studentsQuery = query(collection(db, "users"), where("role", "==", "student"), where("assignedPapers", "array-contains", paper.id));
            const studentsSnap = await getDocs(studentsQuery);
            const studentsList = studentsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setAllStudents(studentsList);

            // B. Fetch Submissions (Using onSnapshot for Real-time Chat/Status)
            const subQuery = query(collection(db, "homeworkSubmissions"), where("homeworkId", "==", hw.id));
            
            // Note: We use a real-time listener here so "New Message" and Status badges update instantly
            const unsubscribe = onSnapshot(subQuery, (snapshot) => {
                const subsList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                setSubmissions(subsList);
                setIsLoadingStats(false);
            });

        } catch (error) {
            console.error("Error fetching stats:", error);
            setModal({ show: true, message: "Error loading submissions." });
            setIsLoadingStats(false);
        }
    };

    // 5. Download Excel Report
    const downloadReport = () => {
        if (!selectedHomework) return;

        const reportData = allStudents.map(student => {
            const submission = submissions.find(s => s.userId === student.id);
            let statusText = "Not Attended";
            if (submission) {
                statusText = submission.status === 'reviewed' ? "Reviewed" : "Submitted";
            }

            return {
                "Student Name": student.name || student.email,
                "Email": student.email,
                "Status": statusText,
                "Submission Date": submission ? new Date(submission.submittedAt?.seconds * 1000).toLocaleString() : "-",
                "Unread Messages": submission?.unreadByAdmin ? "Yes" : "No"
            };
        });

        const ws = XLSX.utils.json_to_sheet(reportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Attendance Report");
        XLSX.writeFile(wb, `${selectedHomework.title}_Report.xlsx`);
    };

    return (
        <div className="bg-white p-6 rounded-lg shadow">
            {/* Header */}
            <div className="flex items-center justify-between mb-6 border-b pb-4">
                <div className="flex items-center gap-4">
                    <button onClick={onBack} className="text-purple-600 hover:underline font-semibold">&larr; Back to Papers</button>
                    <h2 className="text-2xl font-bold text-gray-800">Homework: {paper.name}</h2>
                </div>
            </div>

            {/* Upload Section */}
            {!selectedHomework && (
                <form onSubmit={handleUpload} className="p-6 border rounded-lg bg-gray-50 mb-8">
                    <h3 className="font-bold text-lg text-gray-700 mb-4">Assign New Homework</h3>
                    <div className="flex gap-4 items-end">
                        <div className="flex-1">
                            <label className="block text-sm font-bold text-gray-600 mb-1">Title</label>
                            <input type="text" value={title} onChange={e => setTitle(e.target.value)} className="w-full p-2 border rounded" required placeholder="e.g. Week 3 Assignment" />
                        </div>
                        <div className="flex-1">
                            <label className="block text-sm font-bold text-gray-600 mb-1">Question File (PDF/Image)</label>
                            <input name="file" type="file" accept=".pdf, .jpg, .png, .jpeg" className="w-full p-2 border rounded bg-white" required />
                        </div>
                        <button type="submit" disabled={isUploading} className="bg-purple-600 text-white font-bold px-6 py-2.5 rounded hover:bg-purple-700 disabled:opacity-50">
                            {isUploading ? <Spinner /> : 'Assign'}
                        </button>
                    </div>
                </form>
            )}

            {/* Submissions / Report View */}
            {selectedHomework && (
                <div className="mb-8 p-4 border border-purple-200 rounded-lg bg-purple-50 animate-fade-in">
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="font-bold text-xl text-purple-800">Submissions for: {selectedHomework.title}</h3>
                        <div className="flex gap-2">
                            <button onClick={downloadReport} className="bg-green-600 text-white px-4 py-2 rounded font-bold hover:bg-green-700 flex items-center gap-2">
                                <span>📊</span> Download Report
                            </button>
                            <button onClick={() => setSelectedHomework(null)} className="bg-gray-300 text-gray-800 px-4 py-2 rounded hover:bg-gray-400">Close</button>
                        </div>
                    </div>

                    {isLoadingStats ? <div className="flex justify-center p-4"><Spinner color="border-purple-600" /></div> : (
                        <div className="bg-white rounded border overflow-hidden max-h-96 overflow-y-auto">
                            <table className="w-full text-sm text-left">
                                <thead className="bg-gray-100 text-gray-600 font-bold border-b">
                                    <tr>
                                        <th className="p-3">Student Name</th>
                                        <th className="p-3">Status</th>
                                        <th className="p-3">Last Activity</th>
                                        <th className="p-3">Messages</th>
                                        <th className="p-3">Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {allStudents.map(student => {
                                        const sub = submissions.find(s => s.userId === student.id);
                                        return (
                                            <tr key={student.id} className="border-b hover:bg-gray-50">
                                                <td className="p-3 font-medium">{student.name || student.email}</td>
                                                
                                                {/* --- NEW: STATUS COLUMN --- */}
                                                <td className="p-3">
                                                    {!sub ? (
                                                        <span className="text-red-500 font-semibold">Not Attended</span>
                                                    ) : sub.status === 'reviewed' ? (
                                                        <span className="bg-green-100 text-green-700 px-2 py-1 rounded-full text-xs font-bold border border-green-200">
                                                            ✅ Correction Sent
                                                        </span>
                                                    ) : (
                                                        <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded-full text-xs font-bold border border-blue-200">
                                                            📤 Submitted
                                                        </span>
                                                    )}
                                                </td>

                                                <td className="p-3">{sub ? new Date(sub.submittedAt?.seconds * 1000).toLocaleString() : '-'}</td>
                                                
                                                {/* Chat Status Column */}
                                                <td className="p-3">
                                                    {sub?.unreadByAdmin ? (
                                                        <span className="bg-red-100 text-red-600 px-2 py-1 rounded-full text-xs font-bold border border-red-200 animate-pulse">
                                                            💬 New Message
                                                        </span>
                                                    ) : (
                                                        <span className="text-gray-400 text-xs">-</span>
                                                    )}
                                                </td>

                                                <td className="p-3">
                                                    {sub ? (
                                                        <button 
                                                            onClick={() => onOpenHomework(selectedHomework, student, sub)} 
                                                            className="text-blue-600 hover:underline font-bold"
                                                        >
                                                            Review & Chat
                                                        </button>
                                                    ) : <span className="text-gray-400">-</span>}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            {/* Homework List */}
            {!selectedHomework && (
                <div className="space-y-3">
                    <h3 className="font-bold text-lg text-gray-800">Active Assignments</h3>
                    {homeworks.length === 0 ? <p className="text-gray-500 italic">No homeworks created for this paper yet.</p> : (
                        homeworks.map(hw => (
                            <div key={hw.id} className="flex justify-between items-center p-4 border rounded-lg bg-white hover:shadow-md transition">
                                <div className="flex items-center gap-3">
                                    <span className="text-2xl">📝</span>
                                    <div>
                                        <h4 className="font-bold text-gray-800">{hw.title}</h4>
                                        <p className="text-xs text-gray-500">Posted: {new Date(hw.createdAt?.seconds * 1000).toLocaleDateString()}</p>
                                    </div>
                                </div>
                                <div className="flex gap-3">
                                    <button onClick={() => handleViewSubmissions(hw)} className="px-3 py-1 bg-blue-50 text-blue-600 border border-blue-200 rounded font-semibold text-sm hover:bg-blue-100">
                                        View Submissions
                                    </button>
                                    <button onClick={() => handleDelete(hw.id)} className="px-3 py-1 bg-red-50 text-red-600 border border-red-200 rounded font-semibold text-sm hover:bg-red-100">
                                        Delete
                                    </button>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            )}
        </div>
    );
};

export default HomeworkManager;